import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Autocomplete,
  Paper,
  Typography,
  Box,
} from "@mui/material";
import { FormsIds, DocumentExtensions } from "../../Utils/Constants";
import { Vocabulary } from "../../Utils/Vocabulary";
import { postData } from "../../Services/postData";
import { updateData } from "../../Services/updateData";
import { endpoints } from "../../Utils/UrlEnum";
import { toast } from "react-toastify";
import DropzoneComponent from "../DropzoneComponent";
import { useState } from "react";
import useCustomMemo from "../../Hooks/useCustomMemo";

interface DocumentTemplateFormProps {
  onClose: () => void;
  selectedRow?: any;
  documentTypes?: any[];
}

const validationSchema = yup.object({
  name: yup.string().required(Vocabulary.requiredField),
  documentTypeId: yup.string().required(Vocabulary.requiredField),
  template: yup.mixed().required(Vocabulary.requiredField),
  examCategory: yup.string(),
});

function DocumentTemplateForm({
  onClose,
  selectedRow,
  documentTypes = [],
}: DocumentTemplateFormProps) {
  const [templateFiles, setTemplateFiles] = useState<any[]>([]);
  const memo = useCustomMemo();
  const examCategories = memo.storage.examCategories;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: selectedRow?.name || "",
      documentTypeId: selectedRow?.documentTypeId || "",
      template: selectedRow?.template || null,
      examCategory: selectedRow?.examCategory || "",
    },
  });

  const onSubmit = async (data: any) => {
    try {
      const formData = new FormData();
      formData.append("name", data.name);
      formData.append("documentTypeId", data.documentTypeId);
      if (data.examCategory) {
        formData.append("examCategory", data.examCategory);
      }

      if (templateFiles.length > 0) {
        formData.append("template", templateFiles[0]);
      }

      if (selectedRow?._id) {
        // Update existing document template
        await updateData(
          `${endpoints.documentTemplates}/${selectedRow._id}`,
          formData
        );
      } else {
        // Create new document template
        await postData(endpoints.documentTemplates, formData);
      }
      onClose();
    } catch (error) {
      toast.error(Vocabulary.error);
    }
  };

  return (
    <form id={FormsIds.documentTemplateForm} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            {...register("name")}
            label={Vocabulary.name}
            fullWidth
            error={!!errors.name}
            helperText={errors.name?.message?.toString()}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth error={!!errors.documentTypeId}>
            <InputLabel>{Vocabulary.type}</InputLabel>
            <Controller
              name="documentTypeId"
              control={control}
              render={({ field }) => (
                <Select {...field} label={Vocabulary.type}>
                  {documentTypes.map((option: any) => (
                    <MenuItem key={option._id} value={option._id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            {errors.documentTypeId && (
              <FormHelperText>
                {errors.documentTypeId?.message?.toString()}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="examCategory"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={Object.keys(examCategories ?? []) ?? []}
                getOptionLabel={(option: any) => examCategories?.[option] || ""}
                value={field.value || null}
                onChange={(_, newValue) => {
                  field.onChange(newValue || "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={Vocabulary.examCategory}
                    fullWidth
                    variant="outlined"
                    error={!!errors.examCategory}
                    helperText={errors.examCategory?.message?.toString()}
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="template"
            control={control}
            render={({ field }) => (
              <DropzoneComponent
                onSave={(files) => {
                  setTemplateFiles(files);
                  field.onChange(files.length > 0 ? files[0] : null);
                }}
                acceptedFileTypes={DocumentExtensions}
                maxFiles={1}
                multiple={false}
                altText={Vocabulary.template}
                error={errors.template}
              />
            )}
          />
          {errors.template && (
            <p
              style={{
                color: "red",
                fontSize: "0.875rem",
                marginTop: "0.5rem",
              }}
            >
              {errors.template.message?.toString()}
            </p>
          )}
        </Grid>
        <Grid item xs={12}>
          <Paper
            elevation={1}
            sx={{
              p: 2,
              backgroundColor: "#f5f5f5",
              border: "1px solid #e0e0e0",
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                <strong>Notă:</strong> Dacă nu este selectată o categorie de
                examinare, acest șablon se aplică la toate categoriile. A fost
                creat pentru certificatul de absolvire sau documentele generale.
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </form>
  );
}

export default DocumentTemplateForm;
